import { motion } from "framer-motion";
import { useRef } from "react";

function FeaturesSection() {
  const containerRef = useRef<HTMLDivElement>(null);

  const features = [
    {
      title: "Smart Email Categorization",
      description: "Our AI automatically sorts your emails into customizable categories like Work, Personal, Promotions, and more.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      fromLeft: true
    },
    {
      title: "Bulk Action Recommendations",
      description: "Get personalized suggestions for emails to archive, delete, or unsubscribe from, all with a single click.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      fromLeft: false
    },
    {
      title: "Email Summarization",
      description: "Long emails? No problem. Our AI generates concise summaries so you can quickly grasp the essentials.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h7" />
        </svg>
      ),
      fromLeft: true
    },
    {
      title: "Priority Detection",
      description: "Never miss important emails again. Our AI identifies urgent messages that need your immediate attention.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      fromLeft: false
    },
    {
      title: "Smart Reply Suggestions",
      description: "Generate contextually appropriate responses with one click based on email content and your communication style.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      fromLeft: true
    },
    {
      title: "Newsletter Management",
      description: "Automatically identify subscription emails with one-click unsubscribe options and create organized digests.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      fromLeft: false
    },
    {
      title: "Attachment Organizer",
      description: "Easily find and organize emails with attachments, categorized by type, size, and relevance.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
      ),
      fromLeft: true
    },
    {
      title: "Sentiment Analysis",
      description: "Instantly identify emails with negative or urgent tone so you can prioritize your responses accordingly.",
      icon: (
        <svg className="w-6 h-6 text-[#ff8c42]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      fromLeft: false
    },
  ];

  // Animation variants for container staggering
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  // Different variants for left and right card animations
  const cardLeftVariants = {
    hidden: { 
      x: -60, 
      y: 20,
      opacity: 0,
      rotate: -2,
    },
    visible: { 
      x: 0, 
      y: 0,
      opacity: 1,
      rotate: 0,
      transition: { 
        type: "spring", 
        damping: 15,
        stiffness: 300,
        duration: 0.6
      }
    }
  };

  const cardRightVariants = {
    hidden: { 
      x: 60, 
      y: 20,
      opacity: 0,
      rotate: 2,
    },
    visible: { 
      x: 0, 
      y: 0,
      opacity: 1,
      rotate: 0,
      transition: { 
        type: "spring", 
        damping: 15,
        stiffness: 300,
        duration: 0.6
      }
    }
  };

  return (
    <section id="features" className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-6xl" ref={containerRef}>
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ 
            type: "spring",
            damping: 12,
            stiffness: 200,
            duration: 0.6 
          }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#694e42]">
            AI-Powered Inbox Management
          </h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            MailMaid combines cutting-edge AI features with intuitive design to transform your chaotic inbox into a clean, organized workspace.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              className="p-6 border border-[#e2dad3] rounded-xl bg-white shadow-sm"
              variants={feature.fromLeft ? cardLeftVariants : cardRightVariants}
              whileHover={{ 
                y: -8, 
                boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)",
                transition: { 
                  type: "spring",
                  stiffness: 300,
                  damping: 15
                }
              }}
            >
              <div className="p-3 bg-[#fff5eb] inline-block rounded-lg mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 text-[#694e42]">{feature.title}</h3>
              <p className="text-[#8a7266]">{feature.description}</p>
              
              {/* Subtle animated underline on hover */}
              <motion.div 
                className="w-0 h-0.5 bg-[#ff8c42] mt-4"
                whileHover={{ 
                  width: "100%",
                  transition: { duration: 0.3 }
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default FeaturesSection;